import React from 'react';
import { Link } from 'react-router-dom';
import './StorefrontCampaignHero.css';

const isOnSubdomain = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('.') && !hostname.startsWith('www.');
};

const buildStorePath = (store, path = '/shop') => {
  if (/^https?:\/\//i.test(path)) return path;
  const clean = String(path || '/shop').replace(/^\/+/, '');
  if (isOnSubdomain()) return clean ? `/${clean}` : '/';
  return clean ? `/s/${store?.slug}/${clean}` : `/s/${store?.slug}`;
};

const formatDateRange = (startsAt, endsAt) => {
  const options = { month: 'short', day: 'numeric' };
  const start = startsAt ? new Date(startsAt) : null;
  const end = endsAt ? new Date(endsAt) : null;
  if (!start && !end) return '';
  if (start && end) {
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  }
  if (end) return `Ends ${end.toLocaleDateString('en-US', options)}`;
  return `Starts ${start.toLocaleDateString('en-US', options)}`;
};

const formatOffer = (campaign) => {
  const rawValue = Number(campaign?.value || 0);
  if (!rawValue) return campaign?.badgeText || 'SALE';
  if (campaign?.type === 'PERCENTAGE') return `${rawValue}% off`;
  if (campaign?.type === 'FREE_SHIPPING') return 'Free shipping';
  return 'Limited offer';
};

function StorefrontCampaignHero({ campaign, store, onCtaClick }) {
  if (!campaign?.heroEnabled) return null;

  const title = campaign.heroTitle || campaign.title || campaign.code || 'Limited-time sale';
  const subtitle = campaign.heroSubtitle || campaign.subtitle || 'Selected products are on sale for a limited time.';
  const badge = campaign.badgeText || 'SALE';
  const dateRange = formatDateRange(campaign.startsAt, campaign.endsAt);
  const offer = formatOffer(campaign);
  const ctaText = campaign.heroCtaText || 'Shop deals';
  const ctaLink = buildStorePath(store, campaign.heroCtaLink || '/shop');
  const isExternalCta = /^https?:\/\//i.test(ctaLink);
  const bgColor = campaign.heroBackgroundColor || '#111827';
  const textColor = campaign.heroTextColor || '#ffffff';
  const showPromoCode = campaign.showPromoCode !== false && campaign.code;

  const cta = onCtaClick ? (
    <button type="button" className="sf-campaign-hero__cta" onClick={onCtaClick}>
      {ctaText}
      <span aria-hidden="true">→</span>
    </button>
  ) : isExternalCta ? (
    <a href={ctaLink} className="sf-campaign-hero__cta">
      {ctaText}
      <span aria-hidden="true">→</span>
    </a>
  ) : (
    <Link to={ctaLink} className="sf-campaign-hero__cta">
      {ctaText}
      <span aria-hidden="true">→</span>
    </Link>
  );

  return (
    <section className="sf-campaign-hero" style={{ '--campaign-bg': bgColor, '--campaign-text': textColor }}>
      <div className="sf-campaign-hero__inner">
        <div className="sf-campaign-hero__content">
          <div className="sf-campaign-hero__meta">
            <span className="sf-campaign-hero__badge">{badge}</span>
            {dateRange && <span className="sf-campaign-hero__date">{dateRange}</span>}
          </div>

          <h1 className="sf-campaign-hero__title">{title}</h1>
          {subtitle && <p className="sf-campaign-hero__subtitle">{subtitle}</p>}

          <div className="sf-campaign-hero__actions">
            {cta}
            {showPromoCode && (
              <span className="sf-campaign-hero__code">
                Code <strong>{campaign.code}</strong>
              </span>
            )}
          </div>
        </div>

        <div className="sf-campaign-hero__visual" aria-hidden="true">
          {campaign.heroImage ? (
            <img src={campaign.heroImage} alt="" className="sf-campaign-hero__image" />
          ) : (
            <div className="sf-campaign-hero__offer">
              <span>{offer}</span>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default StorefrontCampaignHero;
